import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AdHistoryLogPage, AdHistoryLogDetail } from '../models/ad-history-log';

const BASE = '/api/amz-advertising/ad-history-logs';

export interface AdHistoryLogListParams {
  offset: number;
  limit: number;
  triggerType?: string;
  action?: string;
  actionFilters?: string;
  startDate?: string;
  endDate?: string;
}

export interface AdDetailLogsParams {
  limit?: number;
  offset?: number;
  search?: string;
  sortField?: string;
  sortDir?: string;
}

export interface AdCombinedDetailsParams {
  ids: string[];
  action: 'PAUSE' | 'ENABLE';
  limit?: number;
  offset?: number;
  search?: string;
  sortField?: string;
  sortDir?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdHistoryLogService {
  constructor(private http: HttpClient) {}

  getList(params: AdHistoryLogListParams): Observable<AdHistoryLogPage> {
    const query: { [key: string]: any } = {
      offset: params.offset,
      limit: params.limit,
    };

    if (params.triggerType) {
      query['triggerType'] = params.triggerType;
    }
    if (params.action) {
      query['action'] = params.action;
    }
    if (params.actionFilters) {
      query['actionFilters'] = params.actionFilters;
    }
    if (params.startDate) {
      query['startDate'] = params.startDate;
    }
    if (params.endDate) {
      query['endDate'] = params.endDate;
    }

    return this.http.get<AdHistoryLogPage>(BASE, { params: query });
  }

  getDetail(
    id: string,
    params?: AdDetailLogsParams,
  ): Observable<AdHistoryLogDetail> {
    const query: { [key: string]: any } = {};
    if (params) {
      if (params.limit != null) {
        query['limit'] = params.limit;
      }
      if (params.offset != null) {
        query['offset'] = params.offset;
      }
      if (params.search) {
        query['search'] = params.search;
      }
      if (params.sortField) {
        query['sortField'] = params.sortField;
      }
      if (params.sortDir) {
        query['sortDir'] = params.sortDir;
      }
    }
    return this.http.get<AdHistoryLogDetail>(BASE + '/' + id, {
      params: query,
    });
  }

  downloadExcel(id: string): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(BASE + '/' + id + '/download', {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const disposition = response.headers.get('Content-Disposition') || '';
          const match = disposition.match(/filename="?([^";\n]+)"?/);
          const filename = match ? match[1].trim() : 'ad-history.xlsx';
          return { blob: response.body as Blob, filename };
        }),
      );
  }

  downloadExcels(ids: string[]): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(BASE + '/download', {
        responseType: 'blob',
        observe: 'response',
        params: { ids: JSON.stringify(ids) },
      })
      .pipe(
        map((response) => {
          const disposition = response.headers.get('Content-Disposition') || '';
          const match = disposition.match(/filename="?([^";\n]+)"?/);
          const filename = match ? match[1].trim() : 'ad-history.xlsx';
          return { blob: response.body as Blob, filename };
        }),
      );
  }

  revert(
    id: string,
  ): Observable<{
    success: boolean;
    revertedCount: number;
    revertLogId?: string;
  }> {
    return this.http.post<{
      success: boolean;
      revertedCount: number;
      revertLogId?: string;
    }>(BASE + '/' + id + '/revert', {});
  }

  reverts(
    ids: string[],
  ): Observable<{
    success: boolean;
    revertedCount: number;
    revertLogIds?: string[];
  }> {
    return this.http.post<{
      success: boolean;
      revertedCount: number;
      revertLogIds?: string[];
    }>(BASE + '/revert', { ids });
  }

  getAutomationStats() {
    return this.http.get<any>(
      `${BASE}/count-automatic`,
    );
  }

  getCombinedDetails(params: AdCombinedDetailsParams) {
    const query: { [key: string]: any } = {
      ids: JSON.stringify(params.ids) || [], 
      action: params.action,
    };
    if (params.limit != null) {
      query['limit'] = params.limit;
    }
    if (params.offset != null) {
      query['offset'] = params.offset;
    }
    if (params.search) {
      query['search'] = params.search;
    }
    if (params.sortField) {
      query['sortField'] = params.sortField;
    }
    if (params.sortDir) {
      query['sortDir'] = params.sortDir;
    }
    return this.http.get<any>(BASE + '/combined-details', {
      params: query,
    });
  }
}
